import React from 'react';

import PropTypes from 'prop-types';

import theme, {
  StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';

import { EXTERNAL_ICON_CLASSNAME } from './consts';
import { SideNavigationItemType } from './SideNavigation';

const SideNavigationItem = React.forwardRef<
  HTMLAnchorElement,
  StylableComponent<HTMLAnchorElement> & {
    children?: React.ReactNode;
    active?: boolean;
    collapsed?: boolean;
    item: SideNavigationItemType;
  }
>(({ style, className, item, active, collapsed, children, ...props }, ref) => (
  <li
    data-collapsed={collapsed}
    css={[
      {
        margin: 0,
        padding: 0,
      },
    ]}
  >
    <a
      {...props}
      ref={ref}
      className={className}
      data-active={active}
      {...(item.onClick ? { onClick: item.onClick } : { href: item.url })}
      css={[
        {
          ...theme.typography.bodyMedium,
          alignItems: 'center',
          color: theme.colors.charcoal700,
          display: 'flex',
          margin: 0,
          padding: '10px 12px',
          textAlign: 'inherit', // Override default text-align on some elements (like button)
          textDecoration: 'none',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          cursor: 'pointer',

          ...(!active && {
            '&:visited': {
              color: theme.colors.charcoal700,
            },
          }),

          '&:hover': {
            color: theme.colors.adskBlue500,

            [`.${EXTERNAL_ICON_CLASSNAME}`]: {
              backgroundColor: theme.colors.adskBlue500,
            },
          },

          '&:active': {
            color: theme.colors.adskBlue700,
          },

          ...(active && {
            background: theme.colors.white,
            boxShadow: `inset 2px 0 0 0 ${theme.colors.adskBlue500}`,
            color: theme.colors.adskBlue500,
          }),
        },
        style,
      ]}
    >
      {children}
    </a>
  </li>
));

SideNavigationItem.displayName = 'SideNavigationItem';

SideNavigationItem.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** children of SideNavigationItem */
  children: PropTypes.node,
  /** Item active state */
  active: PropTypes.bool,
  /** Item collapsed state */
  collapsed: PropTypes.bool,
  /** Item */
  item: PropTypes.object.isRequired,
};

export default SideNavigationItem;
